import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import {
  addSolution,
  attachSolutionFile,
  editSolution,
  levelDescriptionQuery,
  removeSolution,
  solutionEntriesQuery,
  solutionsPageQuery,
  type SolutionAttachment,
  type SolutionCategory,
  type SolutionEntry,
} from '../api/easeedeskSolutions'
import { DataTable, useTableState, type TableColumn } from '../ui/DataTable'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { Content } from '../ui/Shell'
import { SolutionFeedback } from './SolutionFeedback'
import './TicketSolutions.css'

/**
 * "IT Solutions" - `Easeedesk::it_solutions`, macheta
 * `views/version-3/solutions/company.php`.
 *
 * Biblioteca de solutii a firmei: cele zece coloane din
 * `companySubcategoryModel` (public/js/solutions.js), in aceeasi ordine si cu
 * aceleasi titluri - Solution, Level 0..3, Priority, Specific technology,
 * Specific Description, Attachments, Actions.
 *
 * PE `DataTable`, CA CELELALTE LISTE
 * ---------------------------------------------------------------------------
 * Ecranul isi desena singur cate un tabel PE CATEGORIE, si in fiecare inca un
 * tabel, cu propria casuta de cautare si propria paginare. Deasupra tuturor
 * statea un titlu "IT Solutions" - pe care il scrie oricum antetul paginii -
 * si, in dreapta lui, butonul "Master Edit". Cu zece categorii ieseau zece
 * cautari si zece paginari, si niciun loc din care sa cauti o solutie fara sa
 * stii dinainte in ce sertar sta.
 *
 * Acum e o singura lista, cu chromul pe care il are orice lista in portal dupa
 * ce trece `list_chrome.js` peste ea: cautarea si alegerea categoriei in
 * ANTETUL paginii, paginarea si Copy/Columns/Refresh in subsol, nimic deasupra
 * tabelului. Acelasi drum ca la certificate (11 septembrie 2026) - ce statea
 * deasupra tabelului urca in antet, si tabelul ramane singur si lipit sus.
 *
 * CATEGORIA E UN SELECT, NU ZECE BLOCURI. Serverul da randurile pe categorie
 * (`/easeedesk/it-solutions/{id}/entries`), deci oricum se cerea una singura;
 * ce se schimba e ca se alege din antet, de unde se aleg si celelalte filtre
 * ale listelor (`tk-lift-to-header tk-header-select`). Optiunile poarta numele
 * categoriei - in antet nu mai are cine sa le puna eticheta.
 *
 * "Add solution" si "Master Edit" stau in bara de sus, cu `tk-header-extra`,
 * acolo unde `list_chrome.js` urca butoanele proprii ale unui ecran. Master
 * Edit nu mai e un rand de titlu al paginii, ci o unealta a listei, langa
 * celelalte.
 *
 * Cele patru celule de nivel poarta ACEEASI culoare, cea a lui `level_name` al
 * randului: asa desena `createdCell` in portal, care se uita la un singur camp
 * pentru toate patru. Nu s-a "reparat" la mutare - ar fi o alta cifra pe
 * ecran, si nimeni n-a cerut-o.
 *
 * "Attach file" din fereastra de editare (`Solutions::addAttachment`) trimite
 * fisierele la serverul de descarcari, care face si miniaturile si scrie
 * randul; lista se recere dupa. "Master Edit" deschide biblioteca furnizorului
 * din /app (`SolutionsCategoriesPage`).
 */

const TABLE_ID = 'solutionsTable'
const PAGE = 25

/** Textul scris de om, in HTML-ul unei celule. */
function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function ItSolutionsPage() {
  const page = useQuery(solutionsPageQuery)
  const [level, setLevel] = useState<{ subcategory: number; level: string } | null>(null)
  const [add, setAdd] = useState(false)
  const [edit, setEdit] = useState<SolutionEntry | null>(null)
  /* Notele solutiei, din biblioteca - acelasi panou ca pe fila tichetului. */
  const [marks, setMarks] = useState<{ subcategory: number; name: string } | null>(null)
  const client = useQueryClient()

  const categories = page.data?.categories ?? []
  const [picked, setPicked] = useState(0)
  /* Prima categorie cat timp nu s-a ales alta - si tot ea daca cea aleasa a
     disparut intre timp de sub noi. */
  const category = categories.find((one) => one.id === picked) ?? categories[0] ?? null

  const [state, setState] = useTableState(TABLE_ID, {
    start: 0,
    length: PAGE,
    order: { column: 0, dir: 'asc' },
    search: '',
  })

  const list = useQuery({
    ...solutionEntriesQuery(category?.id ?? 0, state.search, state.start, state.length, state.order.dir),
    enabled: category !== null,
    placeholderData: keepPreviousData,
  })

  const columns = useMemo(() => solutionColumns(), [])

  async function onRemove(id: number) {
    if (!window.confirm('Are you sure you want to remove this subcategory?')) return
    try {
      const out = await removeSolution(id)
      if (!out.ok) {
        toast(out.message ?? 'Not removed', 'Error', 'error')
        return
      }
      void client.invalidateQueries({ queryKey: ['easeedesk', 'it-solutions'] })
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Not removed', 'Error', 'error')
    }
  }

  if (page.data && categories.length === 0) {
    return (
      <Content>
        <div className="alert alert-info" style={{ margin: 20 }}>
          <i className="fas fa-info-circle" aria-hidden="true"></i> No solutions categories found.
        </div>
      </Content>
    )
  }

  const rows = (list.data?.data ?? []) as unknown as Record<string, unknown>[]

  return (
    <Content>
      {/*
        Un singur ascultator pentru ce scriu coloanele ca HTML - iconita de
        nivel, Edit si Remove. Celulele sunt siruri, ca in DataTables, deci nu
        au React pe ele; in portal erau prinse tot asa, cu un `on('click')`
        delegat din `solutions.js`.

        Fara `page-content-wrapper`: clasa aia e a paginilor cu meniu lateral
        (`margin-left: 270px` in app.css) si impingea tabelul spre dreapta cu
        un gol de 270px, cand aici nu e niciun meniu.
      */}
      <div
        onClick={(event) => {
          const target = (event.target as HTMLElement).closest<HTMLElement>(
            '.level-description, .od-edit-solution, .od-remove-solution, .od-feedback-solution'
          )
          if (!target) return
          if (target.classList.contains('od-feedback-solution')) {
            setMarks({
              subcategory: Number(target.getAttribute('data-subcategory') ?? 0),
              name: target.getAttribute('data-name') ?? '',
            })
            return
          }
          if (target.classList.contains('level-description')) {
            setLevel({
              subcategory: Number(target.getAttribute('data-subcategory') ?? 0),
              level: target.getAttribute('data-level') ?? '0',
            })
            return
          }
          const id = Number(target.getAttribute('data-entry') ?? 0)
          const entry = (list.data?.data ?? []).find((one) => one.ID === id)
          if (!entry) return
          if (target.classList.contains('od-edit-solution')) setEdit(entry)
          else void onRemove(entry.ID)
        }}
      >
        <DataTable
          id={TABLE_ID}
          columnTools
          columns={columns}
          rows={rows}
          total={list.data?.recordsFiltered ?? 0}
          fetching={list.isFetching}
          error={list.error ? (list.error as Error).message : null}
          onRetry={() => void list.refetch()}
          state={state}
          onState={setState}
          rowId={(row) => `solution_${Number(row.ID)}`}
          emptyHint="A solution is a subcategory with the level, the technology and the description at which it is handled."
          /*
           * CATEGORIA, in bara de sus, langa cautare - `tk-lift-to-header
           * tk-header-select`, locul pe care `list_chrome.css` il tine pentru
           * alegerea proprie a unui ecran (selectul lui Discovered, cel al lui
           * Applications Dashboard). In portal clasa o pune `list_chrome.js`;
           * aici nu exista list_chrome.js, deci o scrie pagina.
           */
          headerView={
            <div className="tk-lift-to-header tk-header-select" style={{ display: 'inline-block', textAlign: 'left' }}>
              <select
                className="form-control input-sm"
                id="solutionCategory"
                title="Category"
                aria-label="Category"
                value={category?.id ?? 0}
                onChange={(e) => {
                  setPicked(Number(e.target.value))
                  setState({ start: 0 })
                }}
              >
                {categories.map((one) => (
                  <option value={one.id} key={one.id}>
                    {one.name}
                  </option>
                ))}
              </select>
            </div>
          }
          headerButtons={
            <>
              <button
                type="button"
                className={`btn btn-success tk-header-extra add-subcategory${category && category.subcategories.length > 0 ? '' : ' disabled'}`}
                disabled={!category || category.subcategories.length === 0}
                title={
                  category && category.subcategories.length === 0
                    ? 'Every solution of this category is already on the list'
                    : 'Add solution'
                }
                onClick={() => setAdd(true)}
              >
                <i className="fas fa-plus" aria-hidden="true"></i> Add solution
              </button>
              {page.data?.masterEdit && (
                <button
                  type="button"
                  className="btn btn-default tk-header-extra"
                  title="Master Edit"
                  onClick={() => window.open('/app/solutions/categories', '_blank', 'noopener')}
                >
                  <i className="fas fa-pencil-alt" aria-hidden="true"></i> Master Edit
                </button>
              )}
            </>
          }
          footerButtons={{ copy: true, colvis: true, refresh: () => void list.refetch() }}
        >
          <AddSolutionModal category={add ? category : null} onClose={() => setAdd(false)} />
          <EditSolutionModal entry={edit} onClose={() => setEdit(null)} />
          <LevelModal ask={level} onClose={() => setLevel(null)} />
          <Modal
            id="solutionFeedbackModal"
            open={marks !== null}
            onClose={() => setMarks(null)}
            title={marks && marks.name !== '' ? `Feedback - ${marks.name}` : 'Feedback'}
          >
            {marks && marks.subcategory > 0 ? <SolutionFeedback subcategory={marks.subcategory} /> : null}
          </Modal>
        </DataTable>
      </div>
    </Content>
  )
}

/** Cele zece coloane din `companySubcategoryModel`, in ordinea din portal. */
function solutionColumns(): TableColumn[] {
  /* Culoarea nivelului sta pe CELULA, ca in `createdCell` - nu se poate pune
     din HTML-ul celulei. */
  const tint = (row: Record<string, unknown>) => {
    const color = String(row.level_color ?? '')
    return color ? { backgroundColor: color } : undefined
  }

  const levels: TableColumn[] = ['0', '1', '2', '3'].map((one, i) => ({
    index: 1 + i,
    title: `Level ${one}`,
    orderable: false,
    visible: true,
    className: 'a-left',
    cellStyle: tint,
    html: (row) =>
      `<span class="fas fa-info-circle center-block level-description" role="button" tabindex="0"` +
      ` style="text-align:center;cursor:pointer" data-subcategory="${Number(row.subcategory_id)}" data-level="${one}"` +
      ` title="Level ${one} description"></span>`,
  }))

  return [
    {
      index: 0,
      title: 'Solution',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => esc(row.subcategory_name),
    },
    ...levels,
    { index: 5, title: 'Priority', orderable: false, visible: true, className: 'a-left', html: (row) => esc(row.priority) },
    {
      index: 6,
      title: 'Specific technology',
      orderable: false,
      visible: true,
      className: 'a-left',
      html: (row) => esc(row.technology),
    },
    {
      index: 7,
      title: 'Specific Description',
      orderable: false,
      visible: true,
      className: 'a-left',
      html: (row) => esc(row.description),
    },
    {
      index: 8,
      title: 'Attachments',
      orderable: false,
      visible: true,
      className: 'center',
      width: 200,
      html: (row) => attachmentsCell((row.attachments ?? []) as SolutionAttachment[]),
    },
    {
      index: 9,
      title: 'Actions',
      orderable: false,
      visible: true,
      className: 'center',
      width: 200,
      html: (row) =>
        `<div class="btn-group" role="group">` +
        `<button type="button" class="btn btn-sm btn-default od-feedback-solution" data-subcategory="${Number(row.subcategory_id)}"` +
        ` data-name="${esc(row.subcategory_name)}" title="Marks and comments">` +
        `<i class="far fa-star" aria-hidden="true"></i> Feedback</button>` +
        `<button type="button" class="btn btn-sm btn-default od-edit-solution" data-entry="${Number(row.ID)}">` +
        `<i class="fas fa-pencil-alt" aria-hidden="true"></i> Edit</button>` +
        `<button type="button" class="btn btn-sm btn-default od-remove-solution remove-subcategory" data-entry="${Number(row.ID)}">` +
        `<i class="fas fa-times" aria-hidden="true"></i> Remove</button>` +
        `</div>`,
    },
  ]
}

function attachmentsCell(files: SolutionAttachment[]): string {
  if (files.length === 0) return 'n/a'
  return files
    .map((file) =>
      file.is_image
        ? `<a href="${esc(file.url)}" title="${esc(file.file_name)}" target="_blank" rel="noreferrer">` +
          (file.thumbnail ? `<img src="${esc(file.thumbnail)}" alt="${esc(file.file_name)}">` : esc(file.file_name)) +
          `</a>`
        : `<a href="${esc(file.url)}">${esc(file.file_name)}</a><br>`
    )
    .join('')
}

function AddSolutionModal({ category, onClose }: { category: SolutionCategory | null; onClose(): void }) {
  const [subcategory, setSubcategory] = useState('0')
  const [technology, setTechnology] = useState('')
  const [description, setDescription] = useState('')
  const client = useQueryClient()

  async function onSubmit() {
    if (!category) return
    try {
      const out = await addSolution(category.id, Number(subcategory), technology, description)
      if (!out.ok) {
        toast(out.message ?? 'Something went wrong.', 'Error', 'error')
        return
      }
      toast('Subcategory added.', 'Success', 'success')
      setSubcategory('0')
      setTechnology('')
      setDescription('')
      onClose()
      void client.invalidateQueries({ queryKey: ['easeedesk', 'it-solutions'] })
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Something went wrong.', 'Error', 'error')
    }
  }

  return (
    <Modal id="addSubcategoryModal" open={category !== null} onClose={onClose} title="Add solution" labelledBy="addSubcategoryModalLabel">
      <div className="modal-body">
        <div className="form-group">
          <label htmlFor="subcategory_id" className="control-label">
            Solution name
          </label>
          <select
            className="form-control"
            id="subcategory_id"
            value={subcategory}
            onChange={(e) => setSubcategory(e.target.value)}
            required
          >
            <option value="0">Nothing selected...</option>
            {(category?.subcategories ?? []).map((one) => (
              <option value={one.ID} key={one.ID}>
                {one.subcategory_name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="add-technology" className="control-label">
            Technology
          </label>
          <input type="text" className="form-control" id="add-technology" value={technology} onChange={(e) => setTechnology(e.target.value)} />
        </div>
        <div className="form-group">
          <label htmlFor="add-description" className="control-label">
            Description
          </label>
          <input type="text" className="form-control" id="add-description" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>
          <button type="button" className="btn btn-primary submit-add-subcategory" onClick={() => void onSubmit()}>
            Create
          </button>
        </div>
      </div>
    </Modal>
  )
}

function EditSolutionModal({ entry, onClose }: { entry: SolutionEntry | null; onClose(): void }) {
  const [level, setLevel] = useState('-1')
  const [technology, setTechnology] = useState('')
  const [description, setDescription] = useState('')
  const [loaded, setLoaded] = useState(0)
  const client = useQueryClient()

  /* Fereastra se umple din randul pe care s-a apasat, ca in `show.bs.modal`. */
  if (entry && loaded !== entry.ID) {
    setLoaded(entry.ID)
    setLevel(entry.level_name === '' ? '-1' : entry.level_name)
    setTechnology(entry.technology)
    setDescription(entry.description)
  }

  async function onSubmit() {
    if (!entry) return
    try {
      const out = await editSolution(entry.ID, level, technology, description)
      if (!out.ok) {
        toast(out.message ?? 'Something went wrong.', 'Error', 'error')
        return
      }
      toast('Solution successfully updated.', 'Success', 'success')
      onClose()
      void client.invalidateQueries({ queryKey: ['easeedesk', 'it-solutions'] })
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Something went wrong.', 'Error', 'error')
    }
  }

  return (
    <Modal id="editSubcategoryModal" open={entry !== null} onClose={onClose} title="Edit solution" labelledBy="editSubcategoryModalLabel">
      <div className="modal-body">
        <div className="form-group">
          <label htmlFor="level_name" className="control-label">
            Level
          </label>
          <select className="form-control" id="level_name" value={level} onChange={(e) => setLevel(e.target.value)}>
            <option value="-1">Select..</option>
            <option value="0">Level 0</option>
            <option value="1">Level 1</option>
            <option value="2">Level 2</option>
            <option value="3">Level 3</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="edit-subcategory-technology" className="control-label">
            Technology
          </label>
          <input
            type="text"
            className="form-control"
            id="edit-subcategory-technology"
            value={technology}
            onChange={(e) => setTechnology(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor="edit-subcategory-description" className="control-label">
            Description
          </label>
          <input
            type="text"
            className="form-control"
            id="edit-subcategory-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        {entry && <AttachFile entry={entry.ID} />}
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>
          <button type="button" className="btn btn-primary submit-edit-subcategory" onClick={() => void onSubmit()}>
            Update
          </button>
        </div>
      </div>
    </Modal>
  )
}

function LevelModal({ ask, onClose }: { ask: { subcategory: number; level: string } | null; onClose(): void }) {
  return (
    <Modal
      id="levelDescriptionModal"
      open={ask !== null}
      onClose={onClose}
      title={ask ? `Level ${ask.level} description` : ''}
      labelledBy="levelDescriptionModalLabel"
    >
      <div className="modal-body" id="levelDescriptionBody">
        {ask ? <LevelBody subcategory={ask.subcategory} level={ask.level} /> : null}
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-default" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  )
}

function LevelBody({ subcategory, level }: { subcategory: number; level: string }) {
  const q = useQuery(levelDescriptionQuery(subcategory, level))

  /* Descrierea e scrisa in editor si se pastreaza asa cum a scris-o omul -
     portalul o punea tot cu `.html()`. */
  return <div dangerouslySetInnerHTML={{ __html: q.data?.description ?? '' }} />
}

/** "Attach file" - `#formUpload` cu `filesToUpload[]`, la `Solutions::addAttachment/{solutie}`. */
function AttachFile({ entry }: { entry: number }) {
  const client = useQueryClient()
  const [files, setFiles] = useState<File[]>([])
  const [busy, setBusy] = useState(false)

  return (
    <div className="form-group">
      <label className="control-label" htmlFor={`solution-files-${entry}`}>
        Attach file
      </label>
      <input id={`solution-files-${entry}`} type="file" name="filesToUpload[]" multiple onChange={(ev) => setFiles(Array.from(ev.target.files ?? []))} />
      <button
        type="button"
        className="btn btn-default btn-sm attach-file-solution"
        disabled={busy || files.length === 0}
        onClick={() => {
          setBusy(true)
          attachSolutionFile(entry, files)
            .then((reply) => {
              toast(reply.message, 'Success', 'success')
              setFiles([])
              void client.invalidateQueries({ queryKey: ['easeedesk', 'it-solutions'] })
            })
            .catch((err: unknown) => toast(err instanceof Error ? err.message : 'File could not be uploaded! Please try again..', 'Error', 'error'))
            .finally(() => setBusy(false))
        }}
      >
        <i className="fas fa-paperclip" aria-hidden="true"></i> Save attachments
      </button>
    </div>
  )
}
